import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CreateNewFolder as FolderIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { getAuthToken } from '../utils/auth';

const apiUrl = process.env.REACT_APP_API_URL;

interface FileItem {
  key: string;
  name: string;
  size: number;
  type: string;
  lastModified: string;
  url: string;
  isFolder: boolean;
}

interface ImageGalleryProps {
  themeMode?: 'light' | 'dark';
  onSelectImage?: (fileUrl: string, fileName: string) => void;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ 
  themeMode = 'dark',
  onSelectImage
}) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPath, setCurrentPath] = useState('images');
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<FileItem | null>(null);

  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchFiles = useCallback(async (path: string = '') => {
    try {
      setLoading(true);
      setError('');
      const token = getAuthToken();
      
      const response = await fetch(`${apiUrl}/prod/file-manager?path=${encodeURIComponent(path)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch files');
      const data = await response.json();
      setFiles(data.files || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch files');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch files when component mounts and when currentPath changes
  useEffect(() => {
    fetchFiles(currentPath);
  }, [currentPath, fetchFiles]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress(0);
      setError('');
      
      const token = getAuthToken();
      let uploadedFiles = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Check file size (10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          setError(`File too large: ${file.name} (${file.size} bytes). Maximum allowed: 10MB`);
          return;
        }

        try {
          // Step 1: Get presigned URL for direct S3 upload
          const presignedResponse = await fetch(`${apiUrl}/prod/file-manager`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              action: 'generatePresignedUrl',
              fileName: file.name,
              contentType: file.type,
              path: currentPath
            })
          });

          if (!presignedResponse.ok) {
            const errorData = await presignedResponse.json();
            throw new Error(errorData.error || 'Failed to get presigned URL');
          }

          const presignedData = await presignedResponse.json();

          // Step 2: Upload directly to S3 using presigned URL
          const uploadResponse = await fetch(presignedData.presignedUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type
            },
            body: file
          });

          if (!uploadResponse.ok) {
            throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
          }

          uploadedFiles++;
          setUploadProgress((uploadedFiles / files.length) * 100);

        } catch (fileError: any) {
          setError(`Failed to upload ${file.name}: ${fileError.message}`);
          return;
        }
      }

      setSuccessMessage(`${uploadedFiles} files uploaded successfully`);
      setUploadProgress(100);
      fetchFiles(currentPath);
    } catch (err: any) {
      setError(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      setError('');
      const token = getAuthToken();
      
      const response = await fetch(`${apiUrl}/prod/file-manager/folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newFolderName,
          path: currentPath
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create folder');
      }

      setSuccessMessage('Folder created successfully');
      setNewFolderName('');
      setShowNewFolderDialog(false);
      fetchFiles(currentPath);
    } catch (err: any) {
      setError(err.message || 'Failed to create folder');
    }
  };

  const handleDeleteFile = async (file: FileItem) => {
    setFileToDelete(file);
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      setError('');
      const token = getAuthToken();
      
      const response = await fetch(`${apiUrl}/prod/file-manager`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          key: fileToDelete.key
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete file');
      }

      setSuccessMessage('File deleted successfully');
      fetchFiles(currentPath);
    } catch (err: any) {
      setError(err.message || 'Failed to delete file');
    } finally {
      setShowDeleteConfirmDialog(false);
      setFileToDelete(null);
    }
  };

  const handleFileClick = (file: FileItem) => {
    if (file.isFolder) {
      // Remove trailing slash from folder key since Lambda adds its own
      const folderPath = file.key.replace(/\/$/, '');
      setCurrentPath(folderPath);
    } else {
      setSelectedFile(file);
      if (onSelectImage) {
        onSelectImage(file.url, file.name);
      }
    }
  };

  const handleNavigateToFolder = (path: string) => {
    setCurrentPath(path);
  };

  const getBreadcrumbs = () => {
    const paths = currentPath.split('/').filter(Boolean);
    const breadcrumbs: { name: string; path: string }[] = [];
    
    let currentPathStr = '';
    paths.forEach((path, index) => {
      currentPathStr = currentPathStr ? `${currentPathStr}/${path}` : path;
      // Show "Home" instead of "images" for the first segment
      const displayName = index === 0 && path === 'images' ? 'Home' : path;
      breadcrumbs.push({ name: displayName, path: currentPathStr });
    });
    
    return breadcrumbs;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImageFile = (file: FileItem) => {
    return file.type.startsWith('image/');
  };

  // Show both folders and images
  const displayFiles = files.filter(file => {
    // Always include folders for navigation
    if (file.isFolder) return true;
    
    // Include image files
    return isImageFile(file);
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: themeMode === 'dark' ? 'white' : '#333' }}>
        Image Gallery
      </Typography>
      
      {/* Breadcrumbs */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs sx={{ color: themeMode === 'dark' ? 'white' : '#333' }}>
          {getBreadcrumbs().map((breadcrumb, index) => (
            <Link
              key={index}
              color="inherit"
              onClick={() => handleNavigateToFolder(breadcrumb.path)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {breadcrumb.name || 'Home'}
            </Link>
          ))}
        </Breadcrumbs>
      </Box>

      {/* Actions */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id="file-upload"
          multiple
          type="file"
          onChange={handleFileUpload}
        />
        <label htmlFor="file-upload">
          <Button
            variant="contained"
            component="span"
            startIcon={<UploadIcon />}
            disabled={uploading}
            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            {uploading ? 'Uploading...' : 'Upload Images'}
          </Button>
        </label>
        
        <Button
          variant="outlined"
          startIcon={<FolderIcon />}
          onClick={() => setShowNewFolderDialog(true)}
          sx={{ 
            borderColor: '#d32f2f', 
            color: '#d32f2f',
            '&:hover': { borderColor: '#b71c1c', bgcolor: 'rgba(211, 47, 47, 0.1)' }
          }}
        >
          New Folder
        </Button>
      </Box>

      {/* Progress Bar */}
      {uploading && (
        <Box sx={{ mb: 2 }}>
          <CircularProgress 
            variant="determinate" 
            value={uploadProgress} 
            size={24}
            sx={{ color: '#d32f2f' }}
          />
          <Typography variant="body2" sx={{ ml: 1, color: themeMode === 'dark' ? 'white' : '#333' }}>
            {uploadProgress}% Complete
          </Typography>
        </Box>
      )}

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Images Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#d32f2f' }} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {displayFiles.map((file) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={file.key}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  bgcolor: themeMode === 'light' ? 'white' : '#2a2a2a',
                  border: selectedFile?.key === file.key ? '2px solid #d32f2f' : '1px solid transparent',
                  '&:hover': {
                    border: '1px solid #d32f2f',
                    boxShadow: themeMode === 'light' 
                      ? '0 4px 20px rgba(211, 47, 47, 0.15)' 
                      : '0 4px 20px rgba(211, 47, 47, 0.3)'
                  }
                }}
                onClick={() => handleFileClick(file)}
              >
                {file.isFolder ? (
                  <Box sx={{ 
                    height: 200, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: themeMode === 'light' ? '#f5f5f5' : '#3a3a3a'
                  }}>
                    <FolderIcon sx={{ fontSize: 64, color: '#ff9800' }} />
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    height="200"
                    image={file.url}
                    alt={file.name}
                    sx={{ objectFit: 'cover' }}
                  />
                )}
                
                <CardContent sx={{ p: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: themeMode === 'dark' ? 'white' : '#333',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {file.name}
                  </Typography>
                  {!file.isFolder && (
                    <Typography 
                      variant="caption" 
                      sx={{ color: themeMode === 'dark' ? '#ccc' : '#666' }}
                    >
                      {formatFileSize(file.size)}
                    </Typography>
                  )}
                </CardContent>
                
                <CardActions sx={{ p: 1, justifyContent: 'space-between' }}>
                  <Chip 
                    label={file.isFolder ? 'FOLDER' : file.type.split('/')[1]?.toUpperCase() || 'IMAGE'} 
                    size="small"
                    sx={{ 
                      bgcolor: file.isFolder ? '#ff9800' : '#2196f3',
                      color: 'white',
                      fontSize: '0.7rem'
                    }}
                  />
                  <Box>
                    {!file.isFolder && (
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(file.url, '_blank');
                        }}
                        title="View"
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFile(file);
                      }}
                      title="Delete"
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {displayFiles.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="body1" sx={{ color: themeMode === 'dark' ? 'white' : '#333' }}>
            No files found in this folder
          </Typography>
        </Box>
      )}

      {/* New Folder Dialog */}
      <Dialog open={showNewFolderDialog} onClose={() => setShowNewFolderDialog(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewFolderDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onClose={() => setShowDeleteConfirmDialog(false)}>
        <DialogTitle>
          Confirm Delete {fileToDelete?.isFolder ? 'Folder' : 'File'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the {fileToDelete?.isFolder ? 'folder' : 'file'} "{fileToDelete?.name}"? 
            {fileToDelete?.isFolder && ' This will also delete all files and subfolders inside it.'} 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirmDialog(false)}>Cancel</Button>
          <Button onClick={confirmDeleteFile} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageGallery; 